import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ShieldCheck, Car, Users, Lock, Zap, Eye, Fence, AlertTriangle } from 'lucide-react';

const SafetyEquipmentPage = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const features = [
    {
      title: 'Vehicle Access Control Systems',
      description: 'Advanced boom barriers, automatic sliding gates, and RFID/UHF vehicle access control systems for secure perimeter management. Our solutions provide automated vehicle identification, access logging, and integration with security management systems for comprehensive facility protection.',
      icon: Car,
      image: 'https://images.unsplash.com/photo-1588362951121-3ee319b018b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxhY2Nlc3MlMjBjb250cm9sfGVufDB8fHx8MTc1MjI0ODI5N3ww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Pedestrian Access Control',
      description: 'Comprehensive turnstiles, tripod gates, flap barriers, and swing barriers for controlled pedestrian access. Our systems feature anti-tailgating technology, emergency evacuation modes, and integration with biometric and card-based authentication systems.',
      icon: Users,
      image: 'https://images.unsplash.com/photo-1569784210445-990e7e44ccfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGJhcnJpZXJzfGVufDB8fHx8MTc1MjI0ODI5MXww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Perimeter Security Systems',
      description: 'Advanced road blockers, tyre killers, and bollards (automatic, manual, and retractable) for critical infrastructure protection. Our systems provide rapid deployment, high-security ratings, and integration with threat detection systems for immediate response capabilities.',
      icon: ShieldCheck,
      image: 'https://images.unsplash.com/photo-1675042809349-3d312e33b1c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGJhcnJpZXJzfGVufDB8fHx8MTc1MjI0ODI5MXww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Anti-Climb & Electric Fencing',
      description: 'High-security anti-climb fencing and electric fence systems designed to deter and detect intrusion attempts. Our solutions include razor wire, electric shock systems, and perimeter intrusion detection with immediate alert capabilities for maximum security.',
      icon: Fence,
      image: 'https://images.pexels.com/photos/1233646/pexels-photo-1233646.jpeg',
    },
    {
      title: 'Biometric Access Systems',
      description: 'Advanced biometric access control systems including fingerprint, facial recognition, and iris scanning technologies. Our solutions provide multi-factor authentication, anti-spoofing measures, and secure credential management for high-security environments.',
      icon: Eye,
      image: 'https://images.unsplash.com/photo-1697382608786-bcf4c113b86e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxhY2Nlc3MlMjBjb250cm9sfGVufDB8fHx8MTc1MjI0ODI5N3ww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Smart Card & Digital Access',
      description: 'Comprehensive smart card, keypad, and QR code access systems with encrypted communication and secure authentication protocols. Our solutions support mobile credentials, time-based access control, and integration with enterprise security systems.',
      icon: Lock,
      image: 'https://images.unsplash.com/photo-1521386455230-4ceaa25b72be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxhY2Nlc3MlMjBjb250cm9sfGVufDB8fHx8MTc1MjI0ODI5N3ww&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'License Plate Recognition (ANPR)',
      description: 'Advanced automatic number plate recognition cameras with real-time processing, database integration, and alert systems. Our ANPR solutions provide vehicle identification, blacklist monitoring, and traffic flow analysis for enhanced security and operations.',
      icon: Eye,
      image: 'https://images.pexels.com/photos/7641991/pexels-photo-7641991.jpeg',
    },
    {
      title: 'Integrated Security Management',
      description: 'Centralized security management systems that integrate all safety equipment into a unified platform. Our solutions provide real-time monitoring, automated responses, incident management, and comprehensive reporting for complete facility security oversight.',
      icon: AlertTriangle,
      image: 'https://images.pexels.com/photos/3023251/pexels-photo-3023251.jpeg',
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1569784210445-990e7e44ccfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGJhcnJpZXJzfGVufDB8fHx8MTc1MjI0ODI5MXww&ixlib=rb-4.1.0&q=85')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 to-near-black/90 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        <div className="container relative z-10">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-16 flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="heading-font text-4xl md:text-6xl font-bold text-off-white">
                  Safety <span className="text-royal-gold">Equipment</span>
                </h1>
              </div>
            </div>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Comprehensive physical security solutions including advanced access control systems, perimeter security barriers, and integrated safety equipment. Our solutions provide multi-layered protection for critical infrastructure, government facilities, and high-security environments.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-gray-dark" ref={ref}>
        <div className="container">
          <div className="space-y-20">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-16 flex items-center justify-center">
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="heading-font text-3xl md:text-4xl font-bold text-off-white">
                        {feature.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-lg text-gray-300 mb-8 leading-relaxed">{feature.description}</p>
                </div>
                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative rounded-24 overflow-hidden group">
                    <img src={feature.image} alt={feature.title} className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-12 flex items-center justify-center">
                        <feature.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SafetyEquipmentPage;